import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import {
  useGetMe,
  useAdminListTrashedTemplates,
  useAdminRestoreTemplate,
  useAdminPurgeTemplate,
  getAdminListTrashedTemplatesQueryKey,
  type AdminTrashedTemplate,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2,
  RotateCcw,
  Trash2,
  ShieldCheck,
  UserCog,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { capitalizeName } from "@/lib/capitalizeName";
import { apiErrorMessage } from "@/lib/apiError";

const PAGE_SIZE = 50;

function ownerDisplayName(owner: AdminTrashedTemplate["owner"]): string {
  const parts = [owner.firstName, owner.lastName].filter(Boolean) as string[];
  if (parts.length > 0) {
    return capitalizeName(parts.join(" ")) ?? parts.join(" ");
  }
  return owner.email ?? owner.clerkUserId ?? `User #${owner.id}`;
}

function ownerAvatarChar(owner: AdminTrashedTemplate["owner"]): string {
  return (
    (owner.firstName?.charAt(0) ||
      owner.lastName?.charAt(0) ||
      owner.email?.charAt(0) ||
      "U").toUpperCase()
  );
}

export default function AdminTemplatesTrashPage() {
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const meQuery = useGetMe();
  const me = meQuery.data;

  const [selectedOwnerId, setSelectedOwnerId] = useState<number | null>(null);
  const [page, setPage] = useState(0);

  const summaryParams = { limit: 200 as const, offset: 0 as const };
  const summaryQuery = useAdminListTrashedTemplates(summaryParams, {
    query: {
      queryKey: getAdminListTrashedTemplatesQueryKey(summaryParams),
      enabled: me?.isAdmin === true,
    },
  });

  const listParams = {
    ...(selectedOwnerId !== null ? { ownerId: selectedOwnerId } : {}),
    limit: PAGE_SIZE as number,
    offset: page * PAGE_SIZE,
  };
  const listQuery = useAdminListTrashedTemplates(listParams, {
    query: {
      queryKey: getAdminListTrashedTemplatesQueryKey(listParams),
      enabled: me?.isAdmin === true,
    },
  });

  useEffect(() => {
    if (meQuery.isLoading) return;
    if (!me) return;
    if (!me.isAdmin) navigate("/app");
  }, [me, meQuery.isLoading, navigate]);

  const restoreMutation = useAdminRestoreTemplate();
  const purgeMutation = useAdminPurgeTemplate();

  const refetchList = () => {
    queryClient.invalidateQueries({
      queryKey: [`/api/admin/templates/trash`],
    });
  };

  const [confirmPurge, setConfirmPurge] = useState<AdminTrashedTemplate | null>(
    null,
  );

  const restoreOne = async (item: AdminTrashedTemplate) => {
    try {
      await restoreMutation.mutateAsync({ id: item.template.id });
      toast.success(
        `Restored "${item.template.name}" for ${ownerDisplayName(item.owner)}`,
      );
      refetchList();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not restore template"));
    }
  };

  const submitPurge = async () => {
    if (!confirmPurge) return;
    try {
      await purgeMutation.mutateAsync({ id: confirmPurge.template.id });
      toast.success(`Permanently deleted "${confirmPurge.template.name}"`);
      setConfirmPurge(null);
      refetchList();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not delete template"));
    }
  };

  const summaryTotal = summaryQuery.data?.total ?? 0;
  const items = listQuery.data?.items ?? [];
  const listTotal = listQuery.data?.total ?? 0;

  const totalPages = Math.max(1, Math.ceil(listTotal / PAGE_SIZE));

  const ownerSummary = useMemo(() => {
    const summaryItems = summaryQuery.data?.items ?? [];
    const byOwner = new Map<
      number,
      { owner: AdminTrashedTemplate["owner"]; count: number }
    >();
    for (const item of summaryItems) {
      const existing = byOwner.get(item.owner.id);
      if (existing) {
        existing.count += 1;
      } else {
        byOwner.set(item.owner.id, { owner: item.owner, count: 1 });
      }
    }
    return Array.from(byOwner.values()).sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return ownerDisplayName(a.owner).localeCompare(ownerDisplayName(b.owner));
    });
  }, [summaryQuery.data?.items]);

  useEffect(() => {
    if (selectedOwnerId === null) return;
    if (!ownerSummary.some((s) => s.owner.id === selectedOwnerId)) {
      setSelectedOwnerId(null);
    }
  }, [ownerSummary, selectedOwnerId]);

  const handleSelectOwner = (ownerId: number | null) => {
    setSelectedOwnerId(ownerId);
    setPage(0);
  };

  const selectedOwnerName =
    selectedOwnerId === null
      ? null
      : ownerSummary.find((s) => s.owner.id === selectedOwnerId)?.owner;

  if (meQuery.isLoading || (me && !me.isAdmin)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="px-6 py-8 max-w-7xl mx-auto w-full">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground mb-2">
            <UserCog className="w-3.5 h-3.5" />
            Admin
          </div>
          <h1 className="text-2xl font-semibold">All trashed templates</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Recover any user's accidentally-deleted template, or delete it
            forever. Built-in templates are not listed here.
          </p>
        </div>
      </div>

      {ownerSummary.length > 0 && (
        <Card className="mb-4" data-testid="admin-trash-owner-summary">
          <CardHeader className="border-b py-3">
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base font-medium">
                Trash by owner
              </CardTitle>
              <span className="text-xs text-muted-foreground">
                Click an owner to filter the list
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-3">
            <div className="flex flex-wrap gap-2">
              {ownerSummary.map(({ owner, count }) => {
                const name = ownerDisplayName(owner);
                const isActive = selectedOwnerId === owner.id;
                return (
                  <button
                    key={owner.id}
                    type="button"
                    onClick={() =>
                      handleSelectOwner(isActive ? null : owner.id)
                    }
                    aria-pressed={isActive}
                    data-testid={`admin-trash-owner-summary-${owner.id}`}
                    className={
                      "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors " +
                      (isActive
                        ? "border-primary bg-primary text-primary-foreground hover:bg-primary/90"
                        : "border-border bg-muted/40 hover:bg-muted")
                    }
                  >
                    <Avatar className="h-5 w-5">
                      <AvatarImage src={undefined} alt={name} />
                      <AvatarFallback className="text-[10px]">
                        {ownerAvatarChar(owner)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="max-w-[12rem] truncate">{name}</span>
                    <Badge
                      variant={isActive ? "outline" : "secondary"}
                      className={
                        "ml-1 px-1.5 py-0 text-[10px] " +
                        (isActive
                          ? "border-primary-foreground/40 text-primary-foreground"
                          : "")
                      }
                      data-testid={`admin-trash-owner-summary-count-${owner.id}`}
                    >
                      {count}
                    </Badge>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="border-b py-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base font-medium">
              {selectedOwnerId === null ? (
                <>
                  {summaryTotal}{" "}
                  {summaryTotal === 1 ? "template" : "templates"} in trash
                </>
              ) : (
                <span
                  className="flex items-center gap-2 flex-wrap"
                  data-testid="admin-trash-filter-active"
                >
                  <span>
                    {listTotal}{" "}
                    {listTotal === 1 ? "template" : "templates"} from{" "}
                    <span className="font-semibold">
                      {selectedOwnerName
                        ? ownerDisplayName(selectedOwnerName)
                        : "this owner"}
                    </span>
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    onClick={() => handleSelectOwner(null)}
                    data-testid="button-admin-trash-clear-filter"
                  >
                    <X className="w-3 h-3 mr-1" />
                    Show all owners
                  </Button>
                </span>
              )}
            </CardTitle>
            <Badge variant="outline" className="gap-1">
              <ShieldCheck className="w-3 h-3" />
              Admin only
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="py-16 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : listQuery.isError ? (
            <div
              className="py-16 text-center text-sm text-destructive"
              data-testid="admin-trash-error"
            >
              {apiErrorMessage(listQuery.error, "Failed to load trashed templates")}
            </div>
          ) : summaryTotal === 0 && selectedOwnerId === null ? (
            <div
              className="py-16 text-center text-sm text-muted-foreground"
              data-testid="admin-trash-empty"
            >
              <Trash2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
              No user templates are currently in the trash.
            </div>
          ) : items.length === 0 ? (
            <div
              className="py-16 text-center text-sm text-muted-foreground"
              data-testid="admin-trash-filter-empty"
            >
              <Trash2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
              No templates match the current owner filter.
              <div className="mt-3">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleSelectOwner(null)}
                >
                  Show all owners
                </Button>
              </div>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Template</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead className="w-32">Category</TableHead>
                    <TableHead className="w-40">Deleted</TableHead>
                    <TableHead className="w-56 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => {
                    const t = item.template;
                    const ownerName = ownerDisplayName(item.owner);
                    return (
                      <TableRow
                        key={t.id}
                        data-testid={`admin-trash-row-${t.id}`}
                      >
                        <TableCell>
                          <div className="min-w-0">
                            <div
                              className="font-medium truncate"
                              data-testid={`admin-trash-name-${t.id}`}
                            >
                              {t.name}
                            </div>
                            <div className="text-xs text-muted-foreground truncate max-w-[28rem]">
                              {t.subject}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 min-w-0">
                            <Avatar className="h-7 w-7">
                              <AvatarImage src={undefined} alt={ownerName} />
                              <AvatarFallback>
                                {ownerAvatarChar(item.owner)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <div
                                className="text-sm font-medium truncate"
                                data-testid={`admin-trash-owner-name-${t.id}`}
                              >
                                {ownerName}
                              </div>
                              {item.owner.email && (
                                <div className="text-xs text-muted-foreground truncate max-w-[16rem]">
                                  {item.owner.email}
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">
                            {t.category}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                          {t.deletedAt
                            ? formatDistanceToNow(new Date(t.deletedAt), {
                                addSuffix: true,
                              })
                            : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="default"
                              disabled={restoreMutation.isPending}
                              onClick={() => void restoreOne(item)}
                              data-testid={`button-admin-restore-${t.id}`}
                            >
                              <RotateCcw className="w-4 h-4 mr-1" />
                              Restore
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              disabled={purgeMutation.isPending}
                              onClick={() => setConfirmPurge(item)}
                              data-testid={`button-admin-purge-${t.id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete forever
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {totalPages > 1 && (
                <div
                  className="flex items-center justify-between border-t px-4 py-3"
                  data-testid="admin-trash-pagination"
                >
                  <span className="text-sm text-muted-foreground">
                    Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, listTotal)} of {listTotal}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={page === 0}
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      data-testid="admin-trash-prev-page"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground px-2">
                      Page {page + 1} of {totalPages}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage((p) => p + 1)}
                      data-testid="admin-trash-next-page"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <AlertDialog
        open={!!confirmPurge}
        onOpenChange={(o) => {
          if (!o && !purgeMutation.isPending) setConfirmPurge(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete forever?</AlertDialogTitle>
            <AlertDialogDescription>
              "{confirmPurge?.template.name}" belongs to{" "}
              <span className="font-medium">
                {confirmPurge ? ownerDisplayName(confirmPurge.owner) : ""}
              </span>
              . This permanently removes the template — it cannot be restored
              afterwards.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={purgeMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void submitPurge();
              }}
              disabled={purgeMutation.isPending}
              data-testid="button-admin-confirm-purge"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {purgeMutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              <Trash2 className="w-4 h-4 mr-1" />
              Delete forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
